import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { vehicleAPI, noticeAPI, analyticsAPI, facilityAPI, paymentAPI, staffAPI, visitorAPI, invoiceAPI, communityAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getAuthToken, getStoredUser, storeAuth } from '../services/auth';
import { useCommunityConfig } from '../services/communityConfig';
import BookingApprovals from '../components/BookingApprovals';
import { VehicleApprovals } from '../components/management/VehicleApprovals';
import CSVUpload from '../components/CSVUpload';
import { InvoicesTab } from '../components/management/InvoicesTab';
import { UsersTab } from '../components/management/UsersTab';
import { StaffTab } from '../components/management/StaffTab';
import { OperationsTab } from '../components/management/OperationsTab';
import { AnalyticsDetailModals } from '../components/management/AnalyticsDetailModals';
import { PetsTab } from '../components/management/PetsTab';
import { GuardRecordsTab } from '../components/management/GuardRecordsTab';
import { VillaOccupancyTile, VillaOccupancyModal } from '../components/management/VillaOccupancy';
import { ExportsTab } from '../components/management/ExportsTab';
import { TCCompliancePanel } from '../components/management/TCCompliancePanel';
import { CommunityToolsPanel } from '../components/management/CommunityToolsPanel';
import { HelpdeskModal } from '../components/helpdesk/HelpdeskModal';
import { 
  LogOut, TrendingUp, Car, Users, Calendar,
  UserPlus, BarChart3, ShieldCheck, MessageSquare
} from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import { NotificationPreferences } from '../components/NotificationPreferences';
import { InstallPushButtons } from '../components/InstallPushButtons';

const CHART_TICK = { fontSize: 12 };
const CHART_TOOLTIP_STYLE = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' };
const CHART_DOT = { fill: '#4f46e5', r: 4 };

export default function ManagementDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const { config } = useCommunityConfig();
  const [analytics, setAnalytics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [notices, setNotices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeStaffCount, setActiveStaffCount] = useState(0);
  const [activeStaffList, setActiveStaffList] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [communityUsers, setCommunityUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSlotManagementModal, setShowSlotManagementModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  const [showVisitorsModal, setShowVisitorsModal] = useState(false);
  const [showActiveVisitorsModal, setShowActiveVisitorsModal] = useState(false);
  const [showActiveStaffModal, setShowActiveStaffModal] = useState(false);
  const [showVehiclesModal, setShowVehiclesModal] = useState(false);
  const [showFacilitiesModal, setShowFacilitiesModal] = useState(false);
  const [showResidentEntriesModal, setShowResidentEntriesModal] = useState(false);
  const [residentEntries, setResidentEntries] = useState([]);
  const [villaOccupancy, setVillaOccupancy] = useState(null);
  const [showVillaOccupancyModal, setShowVillaOccupancyModal] = useState(false);
  const [tcSummary, setTcSummary] = useState(null);
  const [showHelpdeskModal, setShowHelpdeskModal] = useState(false);
  const [helpdeskOpenCount, setHelpdeskOpenCount] = useState(null);
  const [activeTab, setActiveTab] = useState(config.facility_booking_enabled ? 'approvals' : 'users');
  
  const [slotManagementForm, setSlotManagementForm] = useState({ date: '', end_date: '', slot: '', reason: '' });
  const [invoiceForm, setInvoiceForm] = useState({ villa_number: '', amount: '', description: '', due_date: '', category: 'Maintenance' });
  const [staffForm, setStaffForm] = useState({ name: '', id_number: '', id_type: 'Aadhaar', assigned_villas: '', job_description: '', phone: '', emergency_contact: '', employee_id: '', gender: '', staff_type: '' });
  const [noticeForm, setNoticeForm] = useState({ title: '', content: '', priority: 'medium', expires_at: '' });
  const [vehicleForm, setVehicleForm] = useState({ plate_number: '', owner_name: '', villa_number: '', vehicle_type: '', user_role: '', make: '', vehicle_model: '', color: '', is_ev: false });
  const [facilityForm, setFacilityForm] = useState({ name: '', description: '', capacity: 1, room_type: '', charges_per_night: 0, checkin_time: '11:00 AM', checkout_time: '11:00 AM' });
  const [paymentForm, setPaymentForm] = useState({ villa_number: '', amount: '', description: '', due_date: '' });
  const [userForm, setUserForm] = useState({ email: '', name: '', role: '', villa_number: '', phone: '' });

  const loadData = useCallback(async () => {
    try {
      // Refresh user profile from server (picks up super admin changes)
      const token = getAuthToken();
      try {
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (meRes.ok) {
          const freshUser = await meRes.json();
          setUser(freshUser);
          storeAuth(token, freshUser, !!localStorage.getItem('token'));
        }
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Profile refresh failed:', err); }

      const [analyticsRes, vehiclesRes, noticesRes, facilitiesRes, staffRes, activeStaffRes, visitorsRes, invoicesRes] = await Promise.all([
        analyticsAPI.getTraffic(), vehicleAPI.getAll(), noticeAPI.getAll(), facilityAPI.getAll(),
        staffAPI.getAll(), staffAPI.getActive(), visitorAPI.getAll(), invoiceAPI.getAll()
      ]);
      setAnalytics(analyticsRes.data);
      setVehicles(vehiclesRes.data);
      setNotices(noticesRes.data);
      setFacilities(facilitiesRes.data);
      setStaff(staffRes.data);
      setActiveStaffCount(activeStaffRes.data.count);
      setActiveStaffList(activeStaffRes.data.active_staff || []);
      try {
        const [statsRes, usersRes] = await Promise.all([communityAPI.getStats(), communityAPI.getUsers()]);
        setCommunityStats(statsRes.data);
        setCommunityUsers(usersRes.data);
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Community data load failed:', err); }
      setVisitors(visitorsRes.data || []);
      setInvoices(invoicesRes.data || []);
      // Load resident entries and villa occupancy
      try {
        const reRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resident-entries`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (reRes.ok) setResidentEntries(await reRes.json());
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load resident entries:', err); }
      // Load villa occupancy
      try {
        const voRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/community/villa-occupancy`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (voRes.ok) setVillaOccupancy(await voRes.json());
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load villa occupancy:', err); }
      // Load T&C compliance summary
      try {
        const tcRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tc/management/summary`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (tcRes.ok) setTcSummary(await tcRes.json());
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load T&C summary:', err); }
      // Load Helpdesk summary (only if enabled for the community)
      if (config.helpdesk_enabled) {
        try {
          const hRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/helpdesk/_meta/summary`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (hRes.ok) {
            const hJ = await hRes.json();
            setHelpdeskOpenCount(hJ.open ?? 0);
          }
        } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load helpdesk summary:', err); }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Real-time polling: refresh analytics + resident entries every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const [analyticsRes, reRes] = await Promise.all([
          analyticsAPI.getTraffic(),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resident-entries`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        setAnalytics(analyticsRes.data);
        if (reRes.ok) setResidentEntries(await reRes.json());
      } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Polling refresh failed:', err); }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateInvoice = async () => {
    if (!invoiceForm.villa_number || !invoiceForm.amount || !invoiceForm.description || !invoiceForm.due_date) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      await invoiceAPI.create({ villa_number: invoiceForm.villa_number, amount: parseFloat(invoiceForm.amount), description: invoiceForm.description, due_date: invoiceForm.due_date, category: invoiceForm.category });
      toast.success('Invoice created successfully!');
      setShowInvoiceModal(false);
      setInvoiceForm({ villa_number: '', amount: '', description: '', due_date: '', category: 'Maintenance' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create invoice'); }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) return;
    try { await invoiceAPI.cancel(invoiceId); toast.success('Invoice cancelled'); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel invoice'); }
  };

  const handleCreateNotice = async () => {
    if (!noticeForm.title || !noticeForm.content) { toast.error('Please fill all fields'); return; }
    try {
      await noticeAPI.create(noticeForm);
      toast.success('Notice posted successfully!');
      setShowNoticeModal(false);
      setNoticeForm({ title: '', content: '', priority: 'medium', expires_at: '' });
      loadData();
    } catch (error) { toast.error('Failed to post notice'); }
  };

  const handleDeleteNotice = async (id) => {
    try { await noticeAPI.delete(id); toast.success('Notice deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete notice'); }
  };

  const handleAddVehicle = async () => {
    if (!vehicleForm.plate_number || !vehicleForm.villa_number || !vehicleForm.vehicle_type) { toast.error('Please fill required fields (Villa No, Vehicle Type, Plate Number)'); return; }
    try {
      await vehicleAPI.create(vehicleForm);
      toast.success('Vehicle registered successfully!');
      setShowVehicleModal(false);
      setVehicleForm({ plate_number: '', owner_name: '', villa_number: '', vehicle_type: '', user_role: '', make: '', vehicle_model: '', color: '', is_ev: false });
      loadData();
    } catch (error) { toast.error('Failed to register vehicle'); }
  };

  const handleDeleteVehicle = async (id) => {
    try { await vehicleAPI.delete(id); toast.success('Vehicle removed'); loadData(); }
    catch (error) { toast.error('Failed to remove vehicle'); }
  };

  const handleCreateFacility = async () => {
    if (!facilityForm.name || !facilityForm.description) { toast.error('Please fill all fields'); return; }
    try {
      await facilityAPI.create(facilityForm);
      toast.success('Facility created successfully!');
      setShowFacilityModal(false);
      setFacilityForm({ name: '', description: '', capacity: 1, room_type: '', charges_per_night: 0, checkin_time: '11:00 AM', checkout_time: '11:00 AM' });
      loadData();
    } catch (error) { toast.error('Failed to create facility'); }
  };

  const handleDeleteFacility = async (facilityId) => {
    if (!window.confirm('Are you sure you want to delete this facility? This action cannot be undone.')) return;
    try { await facilityAPI.delete(facilityId); toast.success('Facility deleted successfully!'); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete facility'); }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete "${staffName}"? This action cannot be undone.`)) return;
    try { await staffAPI.delete(staffId); toast.success('Staff deleted successfully!'); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete staff'); }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.name || !userForm.role || !userForm.phone) { toast.error('Please fill all required fields'); return; }
    const residentRoles = ['resident_owner', 'non_resident_owner', 'tenant'];
    if (residentRoles.includes(userForm.role) && !userForm.villa_number) { toast.error('Villa number is required for residents'); return; }
    try {
      await communityAPI.createUser({ ...userForm });
      const label = userForm.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      toast.success(`${label} created successfully!`);
      setShowCreateUserModal(false);
      setUserForm({ email: '', name: '', role: '', villa_number: '', phone: '' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDeleteUser = async (userId, userName, userRole) => {
    if (userRole === 'management') { toast.error('Cannot delete management users'); return; }
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This action cannot be undone.`)) return;
    try { await communityAPI.deleteUser(userId); toast.success('User deleted successfully!'); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleEditUserPhone = async (userId, phone) => {
    try {
      await communityAPI.patchUser(userId, { phone });
      toast.success('Phone updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone');
      throw error;
    }
  };

  const handleEditUserEmail = async (userId, email) => {
    try {
      await communityAPI.patchUser(userId, { email });
      toast.success('Login email updated — confirmation sent to both addresses');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
      throw error;
    }
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.villa_number || !paymentForm.amount || !paymentForm.description) { toast.error('Please fill all fields'); return; }
    try {
      await paymentAPI.create(paymentForm);
      toast.success('Invoice generated successfully!');
      setShowPaymentModal(false);
      setPaymentForm({ villa_number: '', amount: '', description: '', due_date: '' });
    } catch (error) { toast.error('Failed to generate invoice'); }
  };

  const handleManageSlots = (facility) => {
    setSelectedFacility(facility);
    setSlotManagementForm({ date: '', slot: '' });
    setShowSlotManagementModal(true);
  };

  const handleBlockSlot = async () => {
    if (!slotManagementForm.date) { toast.error('Please select start date'); return; }
    try {
      const endDate = slotManagementForm.end_date || slotManagementForm.date;
      const reason = slotManagementForm.reason || 'Manual booking';
      await facilityAPI.blockDates(selectedFacility.id, slotManagementForm.date, endDate, reason);
      toast.success('Date(s) blocked successfully!');
      setSlotManagementForm({ date: '', end_date: '', slot: '', reason: '' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to block'); }
  };

  const handleUnblockSlot = async (date, slot) => {
    try { await facilityAPI.unblockSlot(selectedFacility.id, date, slot); toast.success('Slot unblocked!'); loadData(); }
    catch (error) { toast.error('Failed to unblock slot'); }
  };

  const handleCreateStaff = async () => {
    if (!staffForm.name || !staffForm.id_number || !staffForm.assigned_villas) { toast.error('Please fill all required fields (Name, ID Number, Assigned Villas)'); return; }
    try {
      const villas = staffForm.assigned_villas.split(',').map(v => v.trim()).filter(v => v);
      if (villas.length === 0) { toast.error('Please enter at least one villa number'); return; }
      await staffAPI.create({ name: staffForm.name, id_number: staffForm.id_number, id_type: staffForm.id_type || 'Aadhaar', assigned_villas: villas, job_description: staffForm.job_description || 'General', phone: staffForm.phone || '', emergency_contact: staffForm.emergency_contact || '' });
      toast.success('Staff registered successfully!');
      setShowStaffModal(false);
      setStaffForm({ name: '', id_number: '', id_type: 'Aadhaar', assigned_villas: '', job_description: '', phone: '', emergency_contact: '' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to register staff'); }
  };

  const handleLogout = () => { localStorage.clear(); sessionStorage.clear(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gk-bg">
      <header className="bg-gk-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icon-192.png" alt="GateKeeper" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="text-base sm:text-lg font-bold leading-tight">Management Control</h1>
              <p className="text-xs text-slate-300 mt-0.5">{user?.name}</p>
              <p className="text-[10px] sm:text-xs text-gk-teal font-medium">Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              data-testid="profile-icon"
              onClick={() => navigate('/profile')}
              className="w-9 h-9 rounded-full bg-gk-teal flex items-center justify-center cursor-pointer hover:bg-gk-teal-hover transition-colors"
              title="My Profile"
            >
              <span className="text-sm font-bold text-white">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
            </div>
            <NotificationPreferences />
            <NotificationBell />
            <InstallPushButtons />
            <Button data-testid="logout-button" onClick={handleLogout} variant="ghost" className="text-white hover:bg-gk-navy-hl">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20"><p className="text-slate-600">Loading...</p></div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="total-visitors-card" onClick={() => setShowVisitorsModal(true)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Total Visitors</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.total_visitors || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-slate-800" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click for details</p>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="active-visitors-card" onClick={() => setShowActiveVisitorsModal(true)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Active Now</p>
                      <p className="text-3xl font-bold text-emerald-600">{analytics?.active_now || 0}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click for details</p>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="resident-entries-card" onClick={() => setShowResidentEntriesModal(true)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Resident Entries</p>
                      <p className="text-3xl font-bold text-gk-teal">{residentEntries.length}</p>
                    </div>
                    <Car className="w-8 h-8 text-gk-teal" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click for details</p>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="active-staff-card" onClick={() => setShowActiveStaffModal(true)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Active Staff</p>
                      <p className="text-3xl font-bold text-gk-teal">{activeStaffCount}</p>
                    </div>
                    <UserPlus className="w-8 h-8 text-gk-teal" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click for details</p>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="vehicles-card" onClick={() => setShowVehiclesModal(true)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Reg. Vehicles</p>
                      <p className="text-3xl font-bold text-gk-navy-light">{vehicles.length}</p>
                    </div>
                    <Car className="w-8 h-8 text-gk-navy-light" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click for details</p>
                </CardContent>
              </Card>
              {config.facility_booking_enabled && (
                <Card className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" data-testid="facilities-card" onClick={() => setShowFacilitiesModal(true)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Facilities</p>
                        <p className="text-3xl font-bold text-amber-600">{facilities.length}</p>
                      </div>
                      <Calendar className="w-8 h-8 text-amber-600" />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Click for details</p>
                  </CardContent>
                </Card>
              )}
              <VillaOccupancyTile data={villaOccupancy} onClick={() => setShowVillaOccupancyModal(true)} />
              <Card
                className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow"
                data-testid="tc-acceptance-card"
                onClick={() => { setActiveTab('compliance'); setTimeout(() => document.querySelector('[data-testid="tc-compliance-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">T&C Accepted</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        {tcSummary ? `${tcSummary.accepted}/${tcSummary.total_users}` : '—'}
                      </p>
                    </div>
                    <ShieldCheck className="w-8 h-8 text-emerald-700" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {tcSummary ? `${tcSummary.acceptance_pct}% acceptance · click for details` : 'Loading…'}
                  </p>
                </CardContent>
              </Card>
              {config.helpdesk_enabled && (
                <Card
                  className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow"
                  data-testid="management-helpdesk-tile"
                  onClick={() => setShowHelpdeskModal(true)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Helpdesk</p>
                        <p className="text-3xl font-bold text-gk-teal" data-testid="management-helpdesk-open-count">
                          {helpdeskOpenCount ?? '—'}
                        </p>
                      </div>
                      <MessageSquare className="w-8 h-8 text-gk-teal" />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">open + in-progress · click to manage</p>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Traffic Analytics — Today (IST)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  {analytics?.hourly_data && analytics.hourly_data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics.hourly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="time" stroke="#64748b" tick={CHART_TICK} />
                        <YAxis stroke="#64748b" />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={3} dot={CHART_DOT} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-slate-500">No traffic data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 lg:grid-cols-9 bg-slate-200 p-1 rounded-lg h-auto">
                {config.facility_booking_enabled && <TabsTrigger value="approvals" className="data-[state=active]:bg-gk-navy-light data-[state=active]:text-white font-semibold py-2 text-xs">Bookings</TabsTrigger>}
                <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-semibold py-2 text-xs">Users</TabsTrigger>
                <TabsTrigger value="pets" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white font-semibold py-2 text-xs">Pets</TabsTrigger>
                <TabsTrigger value="staff" className="data-[state=active]:bg-gk-teal data-[state=active]:text-white font-semibold py-2 text-xs">Staff</TabsTrigger>
                <TabsTrigger value="guard-records" className="data-[state=active]:bg-gk-navy data-[state=active]:text-white font-semibold py-2 text-xs">Guards</TabsTrigger>
                <TabsTrigger value="csv" className="data-[state=active]:bg-gk-navy-light data-[state=active]:text-white font-semibold py-2 text-xs">CSV Uploads</TabsTrigger>
                <TabsTrigger value="operations" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white font-semibold py-2 text-xs">Operations</TabsTrigger>
                <TabsTrigger value="exports" data-testid="exports-tab-trigger" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white font-semibold py-2 text-xs">Reports</TabsTrigger>
                <TabsTrigger value="compliance" data-testid="compliance-tab-trigger" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white font-semibold py-2 text-xs">Compliance</TabsTrigger>
              </TabsList>

              <TabsContent value="approvals">
                <div className="space-y-6">
                  <VehicleApprovals />
                  <BookingApprovals />
                </div>
              </TabsContent>

              <TabsContent value="users">
                <UsersTab
                  communityStats={communityStats}
                  communityUsers={communityUsers}
                  handleDeleteUser={handleDeleteUser}
                  handleEditUserPhone={handleEditUserPhone}
                  handleEditUserEmail={handleEditUserEmail}
                  showCreateUserModal={showCreateUserModal}
                  setShowCreateUserModal={setShowCreateUserModal}
                  userForm={userForm}
                  setUserForm={setUserForm}
                  handleCreateUser={handleCreateUser}
                />
              </TabsContent>

              <TabsContent value="pets">
                <PetsTab communityUsers={communityUsers} />
              </TabsContent>

              <TabsContent value="staff">
                <StaffTab
                  staff={staff}
                  handleDeleteStaff={handleDeleteStaff}
                  showStaffModal={showStaffModal}
                  setShowStaffModal={setShowStaffModal}
                  staffForm={staffForm}
                  setStaffForm={setStaffForm}
                  handleCreateStaff={handleCreateStaff}
                  onReload={loadData}
                />
              </TabsContent>

              <TabsContent value="guard-records">
                <GuardRecordsTab />
              </TabsContent>

              <TabsContent value="csv">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <CSVUpload type="users" />
                  <CSVUpload type="vehicles" />
                  <CSVUpload type="staff" />
                </div>
              </TabsContent>

              <TabsContent value="operations">
                <OperationsTab
                  notices={notices}
                  vehicles={vehicles}
                  facilities={facilities}
                  handleDeleteNotice={handleDeleteNotice}
                  handleDeleteVehicle={handleDeleteVehicle}
                  handleDeleteFacility={handleDeleteFacility}
                  handleManageSlots={handleManageSlots}
                  reloadData={loadData}
                  showNoticeModal={showNoticeModal}
                  setShowNoticeModal={setShowNoticeModal}
                  noticeForm={noticeForm}
                  setNoticeForm={setNoticeForm}
                  handleCreateNotice={handleCreateNotice}
                  showVehicleModal={showVehicleModal}
                  setShowVehicleModal={setShowVehicleModal}
                  vehicleForm={vehicleForm}
                  setVehicleForm={setVehicleForm}
                  handleAddVehicle={handleAddVehicle}
                  showFacilityModal={showFacilityModal}
                  setShowFacilityModal={setShowFacilityModal}
                  facilityForm={facilityForm}
                  setFacilityForm={setFacilityForm}
                  handleCreateFacility={handleCreateFacility}
                  showPaymentModal={showPaymentModal}
                  setShowPaymentModal={setShowPaymentModal}
                  paymentForm={paymentForm}
                  setPaymentForm={setPaymentForm}
                  handleCreatePayment={handleCreatePayment}
                  showSlotManagementModal={showSlotManagementModal}
                  setShowSlotManagementModal={setShowSlotManagementModal}
                  selectedFacility={selectedFacility}
                  slotManagementForm={slotManagementForm}
                  setSlotManagementForm={setSlotManagementForm}
                  handleBlockSlot={handleBlockSlot}
                  handleUnblockSlot={handleUnblockSlot}
                  config={config}
                />
              </TabsContent>

              <TabsContent value="exports">
                <ExportsTab />
              </TabsContent>

              <TabsContent value="compliance">
                <TCCompliancePanel />
              </TabsContent>
            </Tabs>
            <CommunityToolsPanel />
          </div>
        )}
      </main>

      <AnalyticsDetailModals
        visitors={visitors}
        analytics={analytics}
        activeStaffCount={activeStaffCount}
        activeStaffList={activeStaffList}
        vehicles={vehicles}
        facilities={facilities}
        residentEntries={residentEntries}
        showVisitorsModal={showVisitorsModal}
        setShowVisitorsModal={setShowVisitorsModal}
        showActiveVisitorsModal={showActiveVisitorsModal}
        setShowActiveVisitorsModal={setShowActiveVisitorsModal}
        showActiveStaffModal={showActiveStaffModal}
        setShowActiveStaffModal={setShowActiveStaffModal}
        showVehiclesModal={showVehiclesModal}
        setShowVehiclesModal={setShowVehiclesModal}
        showFacilitiesModal={showFacilitiesModal}
        setShowFacilitiesModal={setShowFacilitiesModal}
        showResidentEntriesModal={showResidentEntriesModal}
        setShowResidentEntriesModal={setShowResidentEntriesModal}
      />
      <VillaOccupancyModal open={showVillaOccupancyModal} onOpenChange={setShowVillaOccupancyModal} />
      <HelpdeskModal open={showHelpdeskModal} onOpenChange={setShowHelpdeskModal} role="management" />
    </div>
  );
}
